<script setup lang="ts">
import { ref, nextTick, onMounted } from 'vue'

interface AiConfig {
  provider: string
  model: string
  enabled: boolean
  dailyLimit: number
}

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  isError?: boolean
}

const isOpen = ref(false)
const isEnabled = ref(false)
const isLoadingConfig = ref(true)
const aiConfig = ref<AiConfig | null>(null)
const messages = ref<ChatMessage[]>([])
const currentInput = ref('')
const isStreaming = ref(false)
const currentSessionId = ref<string | undefined>(undefined)
const messagesEl = ref<HTMLElement | null>(null)
const textareaEl = ref<HTMLTextAreaElement | null>(null)

const SUGGESTED_PROMPTS = [
  'How many subscribers expired today?',
  "What's my revenue this week?",
  'Show me customers at risk of churning',
  'How many active PPPoE subscriptions?',
] as const

async function loadConfig(): Promise<void> {
  isLoadingConfig.value = true
  try {
    const token = localStorage.getItem('auth_token')
    const res = await fetch('/api/ai/config', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) {
      isEnabled.value = false
      return
    }
    const json: { success: boolean; data: AiConfig | null } = await res.json()
    if (json.success && json.data) {
      aiConfig.value = json.data
      isEnabled.value = json.data.enabled
    } else {
      isEnabled.value = false
    }
  } catch {
    isEnabled.value = false
  } finally {
    isLoadingConfig.value = false
  }
}

function toggleDrawer(): void {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    nextTick(() => textareaEl.value?.focus())
  }
}

function closeDrawer(): void {
  isOpen.value = false
}

async function scrollToBottom(): Promise<void> {
  await nextTick()
  if (messagesEl.value) {
    messagesEl.value.scrollTop = messagesEl.value.scrollHeight
  }
}

function useSuggestedPrompt(prompt: string): void {
  currentInput.value = prompt
  nextTick(() => textareaEl.value?.focus())
}

function handleKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    void sendMessage()
  }
}

async function sendMessage(): Promise<void> {
  const text = currentInput.value.trim()
  if (!text || isStreaming.value || !isEnabled.value) return

  currentInput.value = ''

  messages.value.push({ role: 'user', content: text })
  await scrollToBottom()

  // Placeholder for streaming assistant response
  const assistantIdx = messages.value.length
  messages.value.push({ role: 'assistant', content: '' })
  isStreaming.value = true

  try {
    const token = localStorage.getItem('auth_token')
    const res = await fetch('/api/ai/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        message: text,
        ...(currentSessionId.value ? { sessionId: currentSessionId.value } : {}),
      }),
    })

    if (!res.ok || !res.body) {
      messages.value[assistantIdx] = {
        role: 'assistant',
        content: `Error: ${res.status} — could not reach AI assistant.`,
        isError: true,
      }
      isStreaming.value = false
      await scrollToBottom()
      return
    }

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })

      // SSE lines are separated by '\n'; events by '\n\n'
      const lines = buffer.split('\n')
      // Keep the last (potentially incomplete) line in the buffer
      buffer = lines.pop() ?? ''

      for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed.startsWith('data:')) continue

        const payload = trimmed.slice(5).trim()
        if (!payload) continue

        let parsed: { delta?: string; done?: boolean; sessionId?: string; error?: string }
        try {
          parsed = JSON.parse(payload)
        } catch {
          continue
        }

        if (parsed.error) {
          messages.value[assistantIdx] = {
            role: 'assistant',
            content: `Error: ${parsed.error}`,
            isError: true,
          }
          break
        }

        if (parsed.delta) {
          messages.value[assistantIdx] = {
            role: 'assistant',
            content: (messages.value[assistantIdx]?.content ?? '') + parsed.delta,
          }
          await scrollToBottom()
        }

        if (parsed.done) {
          if (parsed.sessionId) {
            currentSessionId.value = parsed.sessionId
          }
        }
      }
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown error'
    messages.value[assistantIdx] = {
      role: 'assistant',
      content: `Connection error: ${msg}`,
      isError: true,
    }
  } finally {
    isStreaming.value = false
    await scrollToBottom()
  }
}

function clearSession(): void {
  messages.value = []
  currentSessionId.value = undefined
  currentInput.value = ''
}

onMounted(() => {
  void loadConfig()
})
</script>

<template>
  <!-- Trigger button (always visible) -->
  <div class="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
    <!-- Chat drawer panel -->
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-4 scale-95"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-4 scale-95"
    >
      <div
        v-if="isOpen"
        class="w-80 sm:w-96 h-[500px] bg-gray-900 border border-gray-700 rounded-xl shadow-2xl flex flex-col overflow-hidden origin-bottom-right"
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700 shrink-0">
          <div class="flex items-center gap-2 min-w-0">
            <!-- Sparkle icon -->
            <svg class="w-4 h-4 text-blue-400 shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path
                d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
              />
            </svg>
            <div class="min-w-0">
              <p class="text-sm font-semibold text-gray-100 leading-tight">AI Assistant</p>
              <p v-if="aiConfig" class="text-xs text-gray-400 truncate leading-tight">
                {{ aiConfig.provider }} / {{ aiConfig.model }}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <!-- Clear session -->
            <button
              v-if="messages.length > 0"
              type="button"
              title="Clear conversation"
              class="p-1 rounded text-gray-400 hover:text-gray-200 hover:bg-gray-700 transition-colors"
              @click="clearSession"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
            <!-- Close -->
            <button
              type="button"
              title="Close"
              class="p-1 rounded text-gray-400 hover:text-gray-200 hover:bg-gray-700 transition-colors"
              @click="closeDrawer"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Disabled / loading state -->
        <div
          v-if="isLoadingConfig"
          class="flex-1 flex items-center justify-center text-gray-500 text-sm"
        >
          <span class="animate-pulse">Loading…</span>
        </div>

        <div
          v-else-if="!isEnabled"
          class="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center"
        >
          <svg class="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
          <p class="text-sm text-gray-400">AI Assistant is currently disabled.</p>
          <p class="text-xs text-gray-500">Enable it in System → AI Settings.</p>
        </div>

        <!-- Active chat UI -->
        <template v-else>
          <!-- Messages area -->
          <div
            ref="messagesEl"
            class="flex-1 overflow-y-auto px-4 py-3 space-y-3 scroll-smooth"
          >
            <!-- Suggested prompts (empty state) -->
            <div v-if="messages.length === 0" class="space-y-2 pt-2">
              <p class="text-xs text-gray-500 text-center mb-3">Suggested questions</p>
              <button
                v-for="prompt in SUGGESTED_PROMPTS"
                :key="prompt"
                type="button"
                class="w-full text-left text-xs text-gray-300 bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-gray-600 rounded-lg px-3 py-2 transition-colors leading-snug"
                @click="useSuggestedPrompt(prompt)"
              >
                {{ prompt }}
              </button>
            </div>

            <!-- Chat messages -->
            <template v-for="(msg, idx) in messages" :key="idx">
              <!-- User message -->
              <div v-if="msg.role === 'user'" class="flex justify-end">
                <div class="max-w-[75%] bg-blue-600 text-white text-sm rounded-2xl rounded-br-sm px-3 py-2 leading-relaxed break-words">
                  {{ msg.content }}
                </div>
              </div>

              <!-- Assistant message -->
              <div v-else class="flex justify-start">
                <div
                  class="max-w-[80%] text-sm rounded-2xl rounded-bl-sm px-3 py-2 leading-relaxed break-words whitespace-pre-wrap"
                  :class="msg.isError ? 'bg-red-900 border border-red-700 text-red-300' : 'bg-gray-700 text-gray-100'"
                >
                  <!-- Streaming typing indicator (empty assistant bubble while streaming) -->
                  <template v-if="msg.content === '' && isStreaming && idx === messages.length - 1">
                    <span class="inline-flex items-center gap-1" aria-label="Typing">
                      <span class="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
                      <span class="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
                      <span class="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
                    </span>
                  </template>
                  <template v-else>{{ msg.content }}</template>
                </div>
              </div>
            </template>
          </div>

          <!-- Input row -->
          <div class="shrink-0 border-t border-gray-700 bg-gray-800 px-3 py-3">
            <div class="flex items-end gap-2">
              <textarea
                ref="textareaEl"
                v-model="currentInput"
                rows="1"
                placeholder="Ask anything…"
                class="flex-1 resize-none bg-gray-900 border border-gray-700 focus:border-blue-500 rounded-lg px-3 py-2 text-sm text-gray-100 placeholder-gray-500 outline-none transition-colors leading-relaxed max-h-24 overflow-y-auto"
                :disabled="isStreaming"
                style="field-sizing: content"
                @keydown="handleKeydown"
              />
              <button
                type="button"
                title="Send"
                class="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
                :class="
                  isStreaming || !currentInput.trim()
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-500 text-white'
                "
                :disabled="isStreaming || !currentInput.trim()"
                @click="sendMessage"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 19V5m0 0l-7 7m7-7l7 7" />
                </svg>
              </button>
            </div>
            <p class="text-xs text-gray-600 mt-1.5 px-1">Enter to send · Shift+Enter for newline</p>
          </div>
        </template>
      </div>
    </Transition>

    <!-- FAB trigger button -->
    <button
      type="button"
      :title="isOpen ? 'Close AI assistant' : 'Open AI assistant'"
      class="relative w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950"
      :class="isOpen ? 'bg-gray-700 hover:bg-gray-600' : 'bg-blue-600 hover:bg-blue-500'"
      @click="toggleDrawer"
    >
      <!-- Sparkle / close icon -->
      <Transition
        enter-active-class="transition-all duration-100"
        enter-from-class="opacity-0 rotate-90 scale-50"
        enter-to-class="opacity-100 rotate-0 scale-100"
        leave-active-class="transition-all duration-100"
        leave-from-class="opacity-100 rotate-0 scale-100"
        leave-to-class="opacity-0 -rotate-90 scale-50"
        mode="out-in"
      >
        <!-- Close icon when open -->
        <svg
          v-if="isOpen"
          key="close"
          class="w-5 h-5 text-gray-300"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
        <!-- Sparkle icon when closed -->
        <svg
          v-else
          key="ai"
          class="w-5 h-5 text-white"
          fill="currentColor"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path
            d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
          />
        </svg>
      </Transition>

      <!-- Online/offline status badge -->
      <span
        v-if="!isLoadingConfig"
        class="absolute top-0.5 right-0.5 w-3 h-3 rounded-full border-2 border-gray-950"
        :class="isEnabled ? 'bg-emerald-500' : 'bg-gray-500'"
        :title="isEnabled ? 'AI online' : 'AI offline'"
        aria-hidden="true"
      />
    </button>
  </div>
</template>
